import type { FC } from 'react'

import { ErrorMessage, Input, Label, LoaderWrapper, Textarea } from '@skytech/ui'
import { useQuery } from '@tanstack/react-query'
import type { FieldProps } from 'formik'
import { Field, useFormikContext } from 'formik'
import { twJoin } from 'tailwind-merge'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import ClassificationTile from '@/modules/ecom-policy/new/steps/step-type/ClassificationTile'
import type { Classification } from '@/modules/ecom-policy/new/types'
import { usePolicyId } from '@/modules/ecom-policy/policy/hooks'
import queries from '@/modules/ecom-policy/queries'
import { ecomPolicyResources } from '@/modules/ecom-policy/resources'
import { leasingResources } from '@/resources/leasing'
import { commonQueries } from '@/resources/queries'

import type { FormFields } from './types'

const Name: FC<{
  isLeasing: boolean
  isDisabled?: boolean
  isPolicyTypeDisabled: boolean
  updatedCategories: string[]
  leasingVendorProductCategories?: { productType: string; monthDuration: number }[]
}> = ({
  isLeasing,
  isDisabled,
  isPolicyTypeDisabled,
  updatedCategories,
  leasingVendorProductCategories,
}) => {
  const translate = useTranslation()
  const policyId = usePolicyId()
  const { values, setValues } = useFormikContext<FormFields>()

  const { data, isLoading } = useQuery(
    queries.categoryClassification(),
    ecomPolicyResources.getCategoryClassification
  )

  const { data: leasingSettings } = useQuery(
    commonQueries.leasingSettingsByPolicyId(policyId),
    () => leasingResources.getLeasingSettings(policyId),
    {
      enabled: !!policyId && isLeasing,
    }
  )
  const isEndOfLeaseEnabled = leasingSettings?.endOfLease?.isActive || false
  const monthDurationByProductType =
    isEndOfLeaseEnabled && leasingVendorProductCategories
      ? Object.fromEntries(
          leasingVendorProductCategories?.map(({ productType, monthDuration }) => [
            productType,
            monthDuration,
          ])
        )
      : {}

  const categoryClassifications =
    (updatedCategories.length > 0
      ? data
          ?.filter(c => updatedCategories.includes(c.id))
          .map(classification => ({
            symbol: classification.id,
            label: translate(classification.name),
          }))
      : data?.map(classification => ({
          symbol: classification.id,
          label: translate(classification.name),
        }))) || []

  return (
    <div>
      <h2>
        <Translation id="Edit name and description" />
      </h2>

      <Field name="name">
        {({ field, meta }: FieldProps) => (
          <Input
            isFullWidth
            id="name"
            label={<Translation id="Name" />}
            type="text"
            marginBottom={16}
            isDisabled={isDisabled}
            error={meta.error}
            touched={meta.touched}
            {...field}
          />
        )}
      </Field>

      <Field name="description">
        {({ field, meta }: FieldProps) => (
          <Textarea
            isFullWidth
            id="description"
            type="text"
            label={<Translation id="Description" />}
            marginBottom={16}
            isDisabled={isDisabled}
            error={meta.error}
            touched={meta.touched}
            {...field}
          />
        )}
      </Field>

      <LoaderWrapper isLoading={isLoading}>
        <Field name="classificationList">
          {({ field, meta, form }: FieldProps) => {
            const selected: Classification[] = field.value || []
            const selectedMonthDuration =
              isEndOfLeaseEnabled && selected.length > 0
                ? monthDurationByProductType[selected[0]]
                : undefined

            return (
              <>
                <Label>{translate('Policy Type')}</Label>
                <div
                  className={twJoin(
                    'mb-2 grid gap-2 rounded-lg border border-solid p-2 sm:grid-cols-1 md:grid-cols-3',
                    !meta.error && 'border-stone-300',
                    meta.error && 'shadow-error border-red-500 bg-red-100'
                  )}
                >
                  {categoryClassifications.map(classificationsListItem => {
                    const isAlreadySelected = selected.includes(
                      classificationsListItem.symbol as Classification
                    )
                    const isIncompatibleDuration =
                      isEndOfLeaseEnabled &&
                      selectedMonthDuration !== undefined &&
                      !isAlreadySelected &&
                      monthDurationByProductType[classificationsListItem.symbol] !==
                        selectedMonthDuration

                    return (
                      <ClassificationTile
                        key={classificationsListItem.symbol}
                        symbol={classificationsListItem.symbol}
                        label={classificationsListItem.label}
                        selectedClassifications={field.value}
                        setSelectedClassifications={(classification: Classification[] | null) => {
                          const updates: Partial<FormFields> = {
                            classificationList: classification,
                          }
                          const newFirstClassification = classification?.[0]
                          const hasDurationData =
                            newFirstClassification !== undefined
                              ? monthDurationByProductType[newFirstClassification] !== undefined
                              : true

                          if (isEndOfLeaseEnabled && !isIncompatibleDuration && hasDurationData) {
                            const newOrderInterval = newFirstClassification
                              ? monthDurationByProductType[newFirstClassification]
                              : undefined
                            if (form.values.orderInterval !== newOrderInterval) {
                              updates.orderInterval = newOrderInterval
                            }
                          }
                          if (Object.keys(updates).length > 0) {
                            setValues({ ...values, ...updates })
                          }
                        }}
                        size="sm"
                        labelClassName="break-words"
                        isDisabled={isDisabled || isPolicyTypeDisabled || isIncompatibleDuration}
                      />
                    )
                  })}
                </div>
                {meta.error && meta.touched && <ErrorMessage>{meta.error}</ErrorMessage>}
              </>
            )
          }}
        </Field>
      </LoaderWrapper>
    </div>
  )
}

export default Name
